import type { Extension, Observation } from "@medplum/fhirtypes";
import { extensionUrl } from "@repo/fhir-systems";
import { OBSERVATION_LEVEL_URL } from "@repo/fhir-systems/extensions";
import {
  DIAGNOSTIC_EXECUTION_DECLARATION_SNAPSHOT_URL,
  DIAGNOSTIC_OBSERVATION_DEFINITION_CODE_SYSTEM,
} from "../../lib/diagnostic-procedure-fhir";
import { fp } from "./codec";
import { required } from "./read";

/**
 * The storage vocabulary the diagnostic procedure's three codecs share.
 *
 * The kind tags, code systems and identity systems themselves live in
 * `lib/diagnostic-procedure-fhir`, which is not a mapper and which the data
 * store already reads. Only what is specific to this aggregate's WRITE side
 * belongs here.
 */

/** The set this execution was ordered from, restated as a plain string beside `basedOn`. */
export const DIAGNOSTIC_ORDER_SET_ID_URL = extensionUrl("diagnostic-order-set-identity");

export const OWNED_PROCEDURE_EXTENSION_URLS = new Set<string>([
  OBSERVATION_LEVEL_URL,
  DIAGNOSTIC_EXECUTION_DECLARATION_SNAPSHOT_URL,
  DIAGNOSTIC_ORDER_SET_ID_URL,
]);

/**
 * Every field the model can EMPTY. The order link and the period are absent on
 * an unordered or a never-started procedure, the declaration snapshot and the
 * order-set id live in `extension`, and a measurement's stored value slot is
 * rewritten wholesale by a correction — including from `component[]` to a
 * top-level `value[x]` and back.
 *
 * Both Observation kinds declare the same list, and the registry unions them
 * per resource type: a row rewritten from one kind into the other has to clear
 * the marker the other kind left, and an owned field named by only one of them
 * would survive that rewrite.
 */
export const PROCEDURE_OWNS = ["basedOn", "performedPeriod", "extension", "note"].map(fp);
export const DIAGNOSTIC_OBSERVATION_OWNS = [
  "valueString",
  "valueQuantity",
  "valueInteger",
  "valueBoolean",
  "valueCodeableConcept",
  "component",
  "extension",
  "note",
].map(fp);

export function extensionOf(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((candidate) => candidate.url === url);
}

/** Child rows cannot infer their patient, visit or owner; the mapper supplies these resolved graph links. */
export interface DiagnosticObservationRowContext {
  subject: Observation["subject"];
  encounter: Observation["encounter"];
  /** The owning procedure, by locator and identity. */
  procedure: NonNullable<Observation["partOf"]>[number];
}

/** Stable member identity for a child row, under the identity system of its kind. */
export function memberIdentity(row: Observation, system: string): string | undefined {
  return row.identifier?.find((identifier) => identifier.system === system)?.value;
}

/** Which declared observation a row realises. Both kinds name it the same way. */
export function definitionIdOf(row: Observation): string {
  return required(
    row,
    "definitionId",
    row.code.coding?.find((coding) => coding.system === DIAGNOSTIC_OBSERVATION_DEFINITION_CODE_SYSTEM)?.code,
  );
}
